import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEnvelope,
  faLink,
  faMessage,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import { SocialPostModal } from "../../socialPostModal";

const DEFAULT_POST_TEXT =
  "Check out my Trackit Verified Stats! #TrackitVerified";
const EMAIL_SUBJECT = "Trackit Verified Stats";
// ShareType sent to /share/send/email: 0 is a comparison, 1 is a player card.
const PLAYER_CARD_SHARE_TYPE = 1;

const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const playerCardFileName = (firstName, lastName) =>
  `${firstName}-${lastName}-player-card.png`;

export const playerCardUrl = (token) =>
  `${window.location.origin}/player-card/${token}`;

const SHARE_PLATFORMS = [
  { platform: "x", label: "X (Twitter)", icon: faXTwitter },
  { platform: "instagram", label: "Instagram", icon: faInstagram },
  { platform: "facebook", label: "Facebook", icon: faFacebook },
  { platform: "email", label: "Email", icon: faEnvelope },
  { platform: "sms", label: "SMS", icon: faMessage, mobileOnly: true },
];

/**
 * Step 2 of the profile card modal: shows the saved card and lets the athlete
 * download it or share it. The image already exists in Spaces, so unlike the
 * Compare share there is no capture or upload here.
 */
export default function ProfileCardReadyStep({
  token,
  imageUrl,
  firstName,
  lastName,
  onClose,
}) {
  const shareRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [postText, setPostText] = useState(DEFAULT_POST_TEXT);
  const [downloading, setDownloading] = useState(false);
  const fileName = playerCardFileName(firstName, lastName);

  // Close on a press outside the trigger and menu. A blur-based close would
  // fire before a menu item's click in Safari, where clicking a button doesn't
  // focus it, and would fight the trigger's own toggle.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (e) => {
      if (!shareRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [menuOpen]);

  const handleOpenMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleOpenPost = (nextPlatform) => {
    setMenuOpen(false);
    setPostText(DEFAULT_POST_TEXT);
    setPlatform(nextPlatform);
  };

  const handleCopyLink = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(playerCardUrl(token));
      toast.success("Card link copied!");
    } catch (error) {
      toast.error("Couldn't copy the card link.");
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error("Couldn't download your card. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="mt-6 flex justify-center">
        <img
          src={imageUrl}
          alt="Your profile card"
          className="w-[640px] max-w-full rounded-xl"
        />
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded border px-4 py-2 text-sm"
        >
          Cancel
        </button>

        <div
          ref={shareRef}
          className="relative"
          onBlur={(e) => {
            // Keyboard users tabbing away. relatedTarget is null for a mouse
            // press in Safari; the pointerdown listener covers that case.
            if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
              setMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            onClick={handleOpenMenu}
            className="flex items-center gap-2 rounded border border-red-500 px-4 py-2 text-sm text-red-500 hover:bg-red-50"
          >
            <FontAwesomeIcon icon={faShareNodes} />
            Share to Social Media
          </button>

          {menuOpen && (
            <div className="absolute bottom-full right-0 z-50 mb-2 w-52 rounded-md border bg-white shadow-lg outline-none">
              {SHARE_PLATFORMS.filter(
                (item) => !item.mobileOnly || isMobile,
              ).map((item) => (
                <button
                  key={item.platform}
                  type="button"
                  onClick={() => handleOpenPost(item.platform)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={item.icon} />
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
              >
                <FontAwesomeIcon icon={faLink} />
                Copy Link
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-2 rounded bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600 disabled:opacity-60"
        >
          <FontAwesomeIcon icon={faDownload} />
          {downloading ? "Downloading..." : "Download Image"}
        </button>
      </div>

      {platform && (
        <SocialPostModal
          platform={platform}
          imageUrl={imageUrl}
          postText={postText}
          setPostText={setPostText}
          onClose={() => setPlatform(null)}
          emailSubject={EMAIL_SUBJECT}
          shareType={PLAYER_CARD_SHARE_TYPE}
          downloadFileName={fileName}
        />
      )}
    </>
  );
}
